import { useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import Link from '@atoms/Link'
import Loading from '@atoms/Loading'
import Button from '@atoms/Button'
import Modal from '@molecules/Modal'
import TextInput from '@molecules/TextInput'
import TextAreaInput from '@molecules/TextAreaInput'
import Selector from '@molecules/Slector'
import CloseIcon from '@icons/Close'
import CheckboxIcon from '@icons/Checkbox'
import { StoreContext } from '@models/Store'
import { useTranslation } from '@models/Translation'
import { sendContact } from '@models/User'
import { sendError } from '@models/Sentry'

import * as style from './style.module.sass'

type FormData = {
  name: string
  email: string
  category: string
  text: string
  agree: boolean
}

const ContactModal = () => {
  const { entity } = useContext(StoreContext)
  const { showContactModal } = useContext(StoreContext).ui
  const { setShowContactModal, addToasts } = useContext(StoreContext).action
  const { t, locale } = useTranslation()

  const methods = useForm<FormData>()
  const { errors } = methods.formState
  const agree = methods.watch('agree')

  const [state, setState] = useState<'edit' | 'loading' | 'complete'>('edit')

  const submit = async (data: FormData) => {
    setState('loading')

    try {
      await sendContact(data.name, data.email, data.category, data.text)
    } catch (error) {
      sendError(error)
      addToasts(t('mypage.failRequest'))
      setState('edit')
      return
    }

    setState('complete')
  }

  useEffect(() => {
    const email = entity.user?.isSignIn ? entity.user.email : ''
    setState('edit')
    methods.reset()
    methods.setValue('email', `${email}`)
  }, [showContactModal])

  return (
    <Modal type="overlay" show={showContactModal}>
      <div className={style.modal}>
        {state === 'complete' ? (
          <>
            <div className={style.complete}>{t('contact.complete')}</div>
            <div
              className={style.completeText}
              dangerouslySetInnerHTML={{ __html: t('contact.completeMessage') }}
            />
            <Button onClick={() => setShowContactModal(false)} design="white">
              {t('mypage.close')}
            </Button>
          </>
        ) : (
          <>
            <div className={style.title}>
              CONTACT
              {locale === 'ja' && <span>{t('contact.form')}</span>}
            </div>
            <div className={style.note}>{t('contact.note')}</div>

            <FormProvider {...methods}>
              <form
                className={style.form}
                onSubmit={methods.handleSubmit(submit)}
              >
                <TextInput
                  name="name"
                  label={t('contact.name')}
                  registerOptions={{ required: true }}
                  placeholder={t('contact.seimei')}
                  errorMessage={
                    errors.name !== undefined
                      ? t('contact.required')
                      : undefined
                  }
                  className={style.input}
                  design="large"
                  disabled={!showContactModal}
                />

                <TextInput
                  name="email"
                  label={t('mypage.email')}
                  registerOptions={{ required: true }}
                  placeholder="xxxxxxx@xxxxx.com"
                  errorMessage={
                    errors.email !== undefined
                      ? t('mypage.correctEmail')
                      : undefined
                  }
                  className={style.input}
                  design="large"
                  disabled={!showContactModal}
                />

                <Selector
                  name="category"
                  selectorItems={[
                    {
                      value: 'サイト全般に関するご質問やご意見',
                      label: t('contact.select1'),
                    },
                    {
                      value: '会員登録やアカウントについて',
                      label: t('contact.select2'),
                    },
                    {
                      value: 'その他のお問い合わせやご意見',
                      label: t('contact.select3'),
                    },
                  ]}
                  label={t('contact.selectItem')}
                  registerOptions={{ required: true }}
                  placeholder={t('contact.selectThis')}
                  errorMessage={
                    errors.category !== undefined
                      ? t('mypage.requireHalf')
                      : undefined
                  }
                  className={style.input}
                />

                <TextAreaInput
                  name="text"
                  label={t('contact.text')}
                  registerOptions={{ required: true }}
                  placeholder={t('contact.textDescription')}
                  errorMessage={
                    errors.text !== undefined
                      ? t('contact.required')
                      : undefined
                  }
                  className={style.input}
                />

                <label className={style.policy}>
                  <input
                    type="checkbox"
                    {...methods.register('agree', { required: true })}
                  />
                  <CheckboxIcon checked={agree} />
                  {locale === 'ja' ? (
                    <>
                      <Link to="/privacy" target="_blank">
                        プライバシーポリシー
                      </Link>
                      に同意します
                    </>
                  ) : (
                    <>
                      I agree to the&nbsp;
                      <Link to="/privacy" target="_blank">
                        privacy policy
                      </Link>
                    </>
                  )}
                </label>

                <button
                  type="submit"
                  className={style.submit}
                  data-loading={state === 'loading'}
                  disabled={state === 'loading'}
                >
                  {state === 'loading' ? (
                    <Loading design="white" size="small" />
                  ) : (
                    t('contact.submit')
                  )}
                </button>
              </form>
            </FormProvider>

            <button
              onClick={() => setShowContactModal(false)}
              className={style.close}
            >
              <CloseIcon />
            </button>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ContactModal
